import type { FC } from 'react';
import { useState } from 'react';

import { useAuth, useBlockSelector } from '@site-builder/block-helpers';
import { TextEditor } from '@site-builder/block-utils';

import * as styles from './CardButtons.css';
import { CartIcon, MinusIcon, PlusIcon } from '../../assets/icons';
import { getCurrencySymbol } from '../../constants';
import { FormattedMessage, formatPrice, isItemPurchased } from '../../utils';
import { useStoreCard } from '../StoreCardWrapper/StoreCardContext';

import type { Item } from '@site-builder/store-sdk';

export const CardButtons: FC = () => {
  const {
    item,
    quantityInCart,
    availableAmount,
    onPurchase,
    onAddToCart,
    onInCartDecrement,
    onInCartIncrement,
  } = useStoreCard();
  const [{ isAuthorized }, { logIn }] = useAuth();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const isPurchased = isItemPurchased(item as Item);
  const isDisabled = isPurchasing || isPurchased;

  const showAmountPicker = quantityInCart > 0;

  const freeButtonText = useBlockSelector(
    (b) => b?.translations?.['free-button'],
  );
  const buyNowText = useBlockSelector((b) => b?.translations?.['buy-now']);

  const handlePurchase = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!isAuthorized) {
      logIn();
      return;
    }

    if (!onPurchase || isPurchasing) {
      return;
    }

    setIsPurchasing(true);
    try {
      const result = onPurchase() as unknown;
      if (result instanceof Promise) {
        result.finally(() => setIsPurchasing(false));
      } else {
        setIsPurchasing(false);
      }
    } catch {
      setIsPurchasing(false);
    }
  };

  const getButtonText = () => {
    if (isPurchasing) {
      return <FormattedMessage id='composer-club.loading' />;
    }

    if (item.isFree) {
      return freeButtonText ? <TextEditor id={freeButtonText} /> : 'Claim';
    }

    if (item.price) {
      const currencySymbol = getCurrencySymbol(item.price.currency);
      const formattedAmount = formatPrice(item.price.amount);
      return `${currencySymbol}${formattedAmount}/month`;
    }

    const virtualPrice = item.getVirtualPrice();
    if (virtualPrice) {
      return `${virtualPrice.amount} ${virtualPrice.name}`;
    }

    return buyNowText ? <TextEditor id={buyNowText} /> : 'Buy Now';
  };

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!isAuthorized) {
      logIn();
      return;
    }
    onAddToCart();
  };

  const handleDecrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onInCartDecrement();
  };

  const handleIncrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onInCartIncrement();
  };

  if (showAmountPicker && !isPurchased) {
    const canIncrement =
      availableAmount > 0 && quantityInCart < availableAmount;

    return (
      <div className={styles.buttonRow}>
        <div className={`msm-button ${styles.amountPicker}`}>
          <button
            type='button'
            onClick={handleDecrement}
            className={styles.amountAdjustButton}
          >
            <MinusIcon />
          </button>
          <span className={styles.amountCount}>{quantityInCart}</span>
          <button
            type='button'
            onClick={handleIncrement}
            disabled={!canIncrement}
            className={styles.amountAdjustButton}
          >
            <PlusIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.buttonRow}>
      <button
        type='button'
        onClick={handlePurchase}
        disabled={isDisabled}
        className={`msm-button ${styles.button}`}
      >
        {getButtonText()}
      </button>
      <button
        type='button'
        onClick={handleAddToCart}
        disabled={isDisabled}
        className={styles.cartButton}
      >
        <CartIcon />
      </button>
    </div>
  );
};
